/**
 * audioController.ts
 * ---------------------------------------------------------
 * Single source of truth for StoryCore media audio state.
 *
 * Responsibilities:
 *  - Track global mute state (with localStorage + __SC_MUTED mirror).
 *  - Track whether audio has been unlocked (WebAudio/HTMLMedia).
 *  - Track whether the user has explicitly allowed audio.
 *  - Provide safePlay() wrapper for gesture-friendly play().
 *  - Provide helpers for reels (registerReel, requestUnmuteFromUserGesture, etc.).
 *  - Broadcast "sc:mutechange" + "sc:audio-state" so HUDs stay in sync.
 * ---------------------------------------------------------
 */

export type AudioUnlockSource =
  | "gesture"
  | "reel"
  | "reel-play-button"
  | "mute-button"
  | "autoplay"
  | "system"
  | "blocked"
  | "hud"
  | "observer"
  | "media"
  | "unlock";

/* ---------------------------------------------------------
   Local storage + event helpers
--------------------------------------------------------- */

const MUTED_STORAGE_KEY_1 = "storycore-audio-muted";
const MUTED_STORAGE_KEY_2 = "sc-muted";
const AUDIO_ALLOWED_KEY = "sc-audio-allowed";

const MUTED_CHANGED_EVENT = "sc:audio-state";

function safeLocalGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key: string, val: string): void {
  try {
    window.localStorage.setItem(key, val);
  } catch {
    // ignore
  }
}

function broadcastDoc(event: string, detail?: any) {
  try {
    document.dispatchEvent(new CustomEvent(event, { detail }));
  } catch {
    // ignore
  }
}

function broadcastWin(event: string, detail?: any) {
  try {
    window.dispatchEvent(new CustomEvent(event, { detail }));
  } catch {
    // ignore
  }
}

/* ---------------------------------------------------------
   Internal State
--------------------------------------------------------- */

// Initialise from persisted storage where possible
let AUDIO_MUTED: boolean = (() => {
  try {
    const v1 = safeLocalGet(MUTED_STORAGE_KEY_1);
    if (v1 === "1" || v1 === "true") return true;
    if (v1 === "0" || v1 === "false") return false;

    const v2 = safeLocalGet(MUTED_STORAGE_KEY_2);
    if (v2 === "1" || v2 === "true") return true;
    if (v2 === "0" || v2 === "false") return false;
  } catch {
    // ignore
  }
  // Default to muted to be safe with autoplay policies
  return true;
})();

let AUDIO_ALLOWED: boolean = (() => {
  try {
    const v = safeLocalGet(AUDIO_ALLOWED_KEY);
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
  } catch {
    // ignore
  }
  return false;
})();

let AUDIO_UNLOCKED = false; // WebAudio + HTMLMedia unlocked at least once
let lastGesture = 0;

// Central registry of active videos (includes reels)
const activeVideos = new Set<HTMLVideoElement>();

// Per-video remembered volume for reels/backgrounds
const rememberedVolume = new WeakMap<HTMLVideoElement, number>();

// Reflect initial state to window for legacy helpers (mediaUtils.isMuted)
try {
  (window as any).__SC_MUTED = AUDIO_MUTED;
} catch {
  // ignore
}

/* ---------------------------------------------------------
   Public getters
--------------------------------------------------------- */

export function isAudioAllowed(): boolean {
  return AUDIO_ALLOWED;
}

export function isAudioMuted(): boolean {
  return AUDIO_MUTED;
}

/**
 * Legacy convenience form used by older mediaUtils code.
 * Mirrors isAudioMuted() but keeps compatibility with older calls.
 */
export function isMuted(): boolean {
  return AUDIO_MUTED;
}

/**
 * Legacy-style "is audio playable" helper:
 *   - Returns true/false if the user has explicitly chosen.
 *   - Returns null if they have not yet made a decision.
 */
export function isAudioPlayable(): boolean | null {
  try {
    const v = safeLocalGet(AUDIO_ALLOWED_KEY);
    if (v === "1" || v === "true") return true;
    if (v === "0" || v === "false") return false;
  } catch {
    // ignore
  }
  return null;
}

export function isUnlocked(): boolean {
  return AUDIO_UNLOCKED;
}

/* ---------------------------------------------------------
   Allowed flag
--------------------------------------------------------- */
export function setAudioAllowed(allowed: boolean): void {
  AUDIO_ALLOWED = !!allowed;
  safeLocalSet(AUDIO_ALLOWED_KEY, allowed ? "1" : "0");
  broadcastWin("sc:audio-allowed", { allowed: AUDIO_ALLOWED });
}

/* ---------------------------------------------------------
   Global mute state
--------------------------------------------------------- */
export function setMuted(
  muted: boolean,
  source: AudioUnlockSource = "system"
): void {
  AUDIO_MUTED = !!muted;

  // Mirror to window-global for older helpers
  try {
    (window as any).__SC_MUTED = AUDIO_MUTED;
  } catch {
    // ignore
  }

  // Persist
  safeLocalSet(MUTED_STORAGE_KEY_1, AUDIO_MUTED ? "1" : "0");
  safeLocalSet(MUTED_STORAGE_KEY_2, AUDIO_MUTED ? "1" : "0");

  // Apply to all registered videos
  for (const v of activeVideos) {
    try {
      v.muted = AUDIO_MUTED;
      if (AUDIO_MUTED) {
        v.setAttribute("muted", "");
      } else {
        v.removeAttribute("muted");
      }
    } catch {
      // ignore
    }
  }

  // Broadcast legacy + modern events
  broadcastWin(MUTED_CHANGED_EVENT, { muted: AUDIO_MUTED });
  broadcastDoc("sc:mutechange", { muted: AUDIO_MUTED, source });
}

/* ---------------------------------------------------------
   Unlock Audio (HTMLMediaElement unlock + policy)
--------------------------------------------------------- */
export async function unlockAudio(from: AudioUnlockSource): Promise<boolean> {
  lastGesture = performance.now();

  try {
    // Simple HTMLMediaElement-based unlock
    const a = new Audio();
    a.muted = false;
    a.volume = 0.01;

    const playAttempt = a.play();
    if (playAttempt instanceof Promise) {
      await playAttempt.catch(() => {});
    }
    a.pause();

    AUDIO_UNLOCKED = true;
    setAudioAllowed(true);
    setMuted(false, from);

    broadcastDoc("sc:audio-unlocked", { source: from });

    return true;
  } catch (err) {
    console.warn("[AudioController] Audio unlock failed:", err);
    return false;
  }
}

/* ---------------------------------------------------------
   Video registry + helpers
--------------------------------------------------------- */

export function registerVideo(video: HTMLVideoElement): void {
  activeVideos.add(video);
  // Immediately reflect global mute state
  try {
    video.muted = AUDIO_MUTED;
    if (AUDIO_MUTED) {
      video.setAttribute("muted", "");
    } else {
      video.removeAttribute("muted");
    }
  } catch {
    // ignore
  }
}

export function unregisterVideo(video: HTMLVideoElement): void {
  activeVideos.delete(video);
}

export function remember(video: HTMLVideoElement): void {
  try {
    const vol = typeof video.volume === "number" ? video.volume : 1;
    rememberedVolume.set(video, vol);
  } catch {
    // ignore
  }
}

export function getRememberedVolume(
  video: HTMLVideoElement,
  fallback: number = 0.8
): number {
  const v = rememberedVolume.get(video);
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  return fallback;
}

/**
 * Ensure a single video is synced to the current global mute state.
 */
export function syncVideoToGlobalMute(video: HTMLVideoElement): void {
  try {
    video.muted = AUDIO_MUTED;
    if (AUDIO_MUTED) {
      video.setAttribute("muted", "");
    } else {
      video.removeAttribute("muted");
    }
  } catch {
    // ignore
  }
}

/* ---------------------------------------------------------
   Safe Play Wrapper
   Ensures: register → unlock (if needed) → sync mute → play.
--------------------------------------------------------- */
export async function safePlay(
  video: HTMLVideoElement,
  source: AudioUnlockSource
): Promise<boolean> {
  registerVideo(video);

  if (!AUDIO_UNLOCKED) {
    await unlockAudio(source);
  }

  // Honor global mute
  syncVideoToGlobalMute(video);

  try {
    const p = video.play();
    if (p instanceof Promise) await p;
  } catch (err) {
    console.warn("[AudioController] safePlay blocked:", err);

    // Fallback: try muted autoplay
    try {
      video.muted = true;
      video.setAttribute("muted", "");
      const p2 = video.play();
      if (p2 instanceof Promise) await p2;
      setMuted(true, "system");
      return !video.paused;
    } catch {
      return false;
    }
  }

  return !video.paused;
}

/* ---------------------------------------------------------
   Manual Unmute / Mute (via UI)
--------------------------------------------------------- */

export async function requestUnmute(
  video: HTMLVideoElement,
  source: AudioUnlockSource = "system"
): Promise<boolean> {
  const ok = await unlockAudio(source);
  if (!ok) return false;

  try {
    video.muted = false;
    video.removeAttribute("muted");
    if (video.volume === 0) {
      video.volume = getRememberedVolume(video, 0.8);
    }
  } catch {
    // ignore
  }

  setMuted(false, source);
  return true;
}

export function requestMute(
  video: HTMLVideoElement,
  source: AudioUnlockSource = "system"
): void {
  try {
    video.muted = true;
    video.setAttribute("muted", "");
  } catch {
    // ignore
  }
  setMuted(true, source);
}

/**
 * Convenience helper specifically for reel HUD mute button:
 *  - Assumes a user gesture.
 *  - Attempts unlock + unmute with sane defaults.
 */
export async function requestUnmuteFromUserGesture(
  video: HTMLVideoElement
): Promise<boolean> {
  const ok = await unlockAudio("mute-button");
  if (!ok) return false;

  try {
    video.muted = false;
    video.removeAttribute("muted");
    if (video.volume === 0) {
      video.volume = getRememberedVolume(video, 0.8);
    }
  } catch {
    // ignore
  }

  setMuted(false, "mute-button");
  return true;
}

/* ---------------------------------------------------------
   Helpers for Reels
--------------------------------------------------------- */

/**
 * Register a reel video with the controller.
 * Marks it as a reel and remembers its starting volume.
 */
export function registerReel(
  video: HTMLVideoElement,
  section?: HTMLElement
): void {
  try {
    if (section) {
      section.dataset.reel = "true";
    }
    (video.dataset as any).reel = "true";
    (video.dataset as any).role = "reel";
  } catch {
    // ignore
  }
  remember(video);
  registerVideo(video);
}

export function unregisterReel(video: HTMLVideoElement): void {
  unregisterVideo(video);
}

/**
 * Attach controller behaviour to an arbitrary video (legacy helper).
 */
export function attachToVideo(video: HTMLVideoElement): void {
  registerVideo(video);
  if (AUDIO_ALLOWED && AUDIO_UNLOCKED && !AUDIO_MUTED) {
    try {
      video.muted = false;
      video.removeAttribute("muted");
    } catch {
      // ignore
    }
  }
}

/* ---------------------------------------------------------
   Aggregate export (object-style) for convenience.
   This allows: import { AudioController } from "@/utils/audioController";
--------------------------------------------------------- */

export const AudioController = {
  // State + getters
  isAudioAllowed,
  isAudioMuted,
  isMuted,
  isAudioPlayable,
  isUnlocked,

  // Flags
  setAudioAllowed,
  setMuted,

  // Unlock
  unlockAudio,

  // Video registry
  registerVideo,
  unregisterVideo,
  remember,
  getRememberedVolume,
  syncVideoToGlobalMute,

  // Playback helpers
  safePlay,

  // UI-facing operations
  requestUnmute,
  requestMute,
  requestUnmuteFromUserGesture,

  // Reels
  registerReel,
  unregisterReel,
  attachToVideo,
};